import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNotify, useTranslate } from "ra-core";
import { ImageUp, Sparkles, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { ExperimentImage } from "./ExperimentImage";
import {
  buildRequest,
  describeStatus,
  elapsedSeconds,
  groupBatches,
  isActive,
  roundStrength,
  stateKey,
  type PlaygroundJob,
  type SeedMode,
} from "./playground";
import {
  cancelPlaygroundJob,
  createPlaygroundJobs,
  uploadPlaygroundImage,
  usePlaygroundJobs,
  usePlaygroundStatus,
} from "./playgroundApi";
import { imageKey } from "./types";
import type {
  ExperimentManifest,
  ExperimentPhoto,
  ExperimentPlayground,
} from "./types";

const CUSTOM = "custom";
const toneClass = {
  ok: "bg-emerald-500",
  busy: "bg-amber-500",
  offline: "bg-red-500",
  unknown: "bg-muted-foreground",
} as const;

function useT() {
  const translate = useTranslate();
  return (key: string, options?: Record<string, unknown>) =>
    translate(`crm.lab_results.playground.${key}`, options);
}

function useNow(active: boolean) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [active]);
  return now;
}

export const PlaygroundPanel = ({
  manifest,
  playground,
  photo,
}: {
  manifest: ExperimentManifest;
  playground: ExperimentPlayground;
  photo: ExperimentPhoto;
}) => {
  const t = useT();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const status = usePlaygroundStatus();
  const history = usePlaygroundJobs(manifest.slug);
  const jobs = useMemo(() => history.data?.jobs ?? [], [history.data]);

  const sourceArm =
    manifest.arms.find((a) => a.kind === "source") ?? manifest.arms[0];
  const [upload, setUpload] = useState<{
    key: string;
    preview: string;
    name: string;
  } | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const [loraId, setLoraId] = useState(
    playground.defaults.lora ?? playground.loras[0].id,
  );
  const [promptId, setPromptId] = useState(
    playground.defaults.prompt ?? playground.prompts[0].id,
  );
  const [customPrompt, setCustomPrompt] = useState(
    playground.prompts.find((p) => p.id === promptId)?.template ?? "",
  );
  const [strength, setStrength] = useState(playground.strength.default);
  const publishedSeed = upload ? undefined : playground.seeds[photo.id];
  const [seedMode, setSeedMode] = useState<SeedMode>("published");
  const [fixedSeed, setFixedSeed] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const lora =
    playground.loras.find((l) => l.id === loraId) ?? playground.loras[0];
  const preset = playground.prompts.find((p) => p.id === promptId);
  const effectiveSeedMode: SeedMode =
    seedMode === "published" && publishedSeed === undefined
      ? "random"
      : seedMode;
  const fixedSeedValue = /^\d{1,15}$/.test(fixedSeed)
    ? Number(fixedSeed)
    : undefined;
  const selected =
    jobs.find((j) => j.job_id === selectedId) ??
    jobs.find((j) => j.photo_id === photo.id) ??
    null;

  const create = useMutation({
    mutationFn: (strengths: number[]) =>
      createPlaygroundJobs(
        buildRequest({
          experiment: manifest.slug,
          source: upload
            ? { kind: "upload", key: upload.key }
            : { kind: "photo", photoId: photo.id },
          loraId: lora.id,
          promptId,
          customPrompt,
          strengths,
          seedMode: effectiveSeedMode,
          publishedSeed,
          fixedSeed: fixedSeedValue,
        }),
        crypto.randomUUID(),
      ),
    onSuccess: (result) => {
      queryClient.setQueryData(
        ["lab-playground", "jobs", manifest.slug],
        (old: { experiment: string; jobs: PlaygroundJob[] } | undefined) => ({
          experiment: manifest.slug,
          jobs: [
            ...result.jobs,
            ...(old?.jobs ?? []).filter(
              (j) => !result.jobs.some((n) => n.job_id === j.job_id),
            ),
          ],
        }),
      );
      setSelectedId(
        result.jobs[Math.floor((result.jobs.length - 1) / 2)]?.job_id ?? null,
      );
      void queryClient.invalidateQueries({ queryKey: ["lab-playground"] });
    },
    onError: (error: Error) =>
      notify(`${t("generate_failed")}: ${error.message}`, { type: "error" }),
  });

  const cancel = useMutation({
    mutationFn: (job: PlaygroundJob) =>
      cancelPlaygroundJob(manifest.slug, job.job_id),
    onSettled: () =>
      void queryClient.invalidateQueries({
        queryKey: ["lab-playground", "jobs", manifest.slug],
      }),
    onError: (error: Error) => notify(error.message, { type: "error" }),
  });

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    try {
      const key = await uploadPlaygroundImage(file);
      setUpload({ key, preview: URL.createObjectURL(file), name: file.name });
    } catch (error) {
      notify(`${t("upload_failed")}: ${(error as Error).message}`, {
        type: "error",
      });
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };
  useEffect(
    () => () => {
      if (upload) URL.revokeObjectURL(upload.preview);
    },
    [upload],
  );

  const pill = describeStatus(status.data);
  const promptReady = promptId !== CUSTOM || customPrompt.trim().length > 0;
  const seedReady =
    effectiveSeedMode !== "fixed" || fixedSeedValue !== undefined;
  const canGenerate =
    promptReady && seedReady && !create.isPending && !uploading;
  const noLora = lora.file === null;
  const sweep = playground.sweepStrengths;

  return (
    <div className="grid gap-4 xl:grid-cols-[400px_1fr]">
      <Card>
        <CardContent className="py-4 space-y-5">
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-2">
              <h2 className="font-semibold">
                {playground.title ?? t("title")}
              </h2>
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span
                  className={`h-2 w-2 rounded-full ${toneClass[pill.tone]}`}
                />
                {status.error
                  ? t("status_error")
                  : t(pill.key, { gpu: pill.gpu ?? "GPU" })}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {playground.description ?? t("subtitle")}
            </p>
          </div>

          <section className="space-y-2">
            <h3 className="text-sm font-medium">{t("input")}</h3>
            <div className="flex items-center gap-3">
              <div className="h-20 w-28 shrink-0 rounded-md overflow-hidden bg-muted">
                {upload ? (
                  <img
                    src={upload.preview}
                    alt={upload.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <ExperimentImage
                    media={manifest.images[imageKey(photo.id, sourceArm.id)]}
                    alt={photo.label ?? photo.id}
                    className="h-full w-full object-cover"
                  />
                )}
              </div>
              <div className="min-w-0 space-y-1 text-xs">
                <p className="truncate font-medium">
                  {upload ? upload.name : (photo.label ?? photo.id)}
                </p>
                <p className="text-muted-foreground">
                  {upload ? t("input_upload_hint") : t("input_photo_hint")}
                </p>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7"
                    disabled={uploading}
                    onClick={() => fileInput.current?.click()}
                  >
                    {uploading ? (
                      <Spinner className="size-3" />
                    ) : (
                      <ImageUp className="h-3 w-3" />
                    )}
                    {t("upload")}
                  </Button>
                  {upload && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7"
                      onClick={() => setUpload(null)}
                    >
                      {t("use_entry_photo")}
                    </Button>
                  )}
                </div>
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  aria-label={t("upload")}
                  onChange={(e) => void onFile(e.target.files?.[0])}
                />
              </div>
            </div>
          </section>

          <section className="space-y-2">
            <h3 className="text-sm font-medium">{t("lora")}</h3>
            <ToggleGroup
              type="single"
              variant="outline"
              value={lora.id}
              onValueChange={(v) => v && setLoraId(v)}
              className="flex-wrap justify-start"
            >
              {playground.loras.map((l) => (
                <ToggleGroupItem
                  key={l.id}
                  value={l.id}
                  className="text-xs px-3"
                  title={l.description}
                >
                  {l.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </section>

          <section className="space-y-2">
            <h3 className="text-sm font-medium">{t("prompt")}</h3>
            <ToggleGroup
              type="single"
              variant="outline"
              value={promptId}
              onValueChange={(v) => {
                if (!v) return;
                if (v === CUSTOM && promptId !== CUSTOM && preset)
                  setCustomPrompt(preset.template);
                setPromptId(v);
              }}
              className="flex-wrap justify-start"
            >
              {playground.prompts.map((p) => (
                <ToggleGroupItem
                  key={p.id}
                  value={p.id}
                  className="text-xs px-3"
                  title={p.description}
                >
                  {p.label}
                </ToggleGroupItem>
              ))}
              {playground.allowCustomPrompt && (
                <ToggleGroupItem value={CUSTOM} className="text-xs px-3">
                  {t("custom")}
                </ToggleGroupItem>
              )}
            </ToggleGroup>
            {promptId === CUSTOM ? (
              <Textarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                rows={4}
                maxLength={2000}
                className="text-xs"
                aria-label={t("custom")}
              />
            ) : (
              <p
                className="text-xs text-muted-foreground line-clamp-4"
                title={preset?.template}
              >
                {preset?.description ?? preset?.template}
              </p>
            )}
            {promptId === CUSTOM && (
              <p className="text-xs text-muted-foreground">
                {t("custom_hint")}
              </p>
            )}
          </section>

          <section className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium">{t("strength")}</h3>
              <span className="text-sm tabular-nums font-medium">
                {noLora ? "—" : strength.toFixed(2)}
              </span>
            </div>
            <input
              type="range"
              min={playground.strength.min}
              max={playground.strength.max}
              step={playground.strength.step}
              value={strength}
              disabled={noLora}
              onChange={(e) =>
                setStrength(
                  roundStrength(
                    Number(e.target.value),
                    playground.strength.step,
                  ),
                )
              }
              className="w-full accent-primary"
              aria-label={t("strength")}
            />
            <div className="flex justify-between text-[11px] text-muted-foreground">
              <span>
                {t("strength_less", { value: playground.strength.min })}
              </span>
              <span>
                {t("strength_more", { value: playground.strength.max })}
              </span>
            </div>
            {noLora && (
              <p className="text-xs text-muted-foreground">
                {t("no_lora_hint")}
              </p>
            )}
          </section>

          <section className="space-y-2">
            <h3 className="text-sm font-medium">{t("seed")}</h3>
            <div className="flex flex-wrap items-center gap-2">
              <ToggleGroup
                type="single"
                variant="outline"
                value={effectiveSeedMode}
                onValueChange={(v) => v && setSeedMode(v as SeedMode)}
                className="justify-start"
              >
                <ToggleGroupItem
                  value="published"
                  className="text-xs px-3"
                  disabled={publishedSeed === undefined}
                  title={
                    publishedSeed !== undefined
                      ? String(publishedSeed)
                      : t("seed_published_missing")
                  }
                >
                  {t("seed_published")}
                </ToggleGroupItem>
                <ToggleGroupItem value="random" className="text-xs px-3">
                  {t("seed_random")}
                </ToggleGroupItem>
                <ToggleGroupItem value="fixed" className="text-xs px-3">
                  {t("seed_fixed")}
                </ToggleGroupItem>
              </ToggleGroup>
              {effectiveSeedMode === "fixed" && (
                <input
                  value={fixedSeed}
                  onChange={(e) =>
                    setFixedSeed(e.target.value.replace(/\D/g, "").slice(0, 15))
                  }
                  inputMode="numeric"
                  placeholder="1234"
                  className="w-32 border rounded-md bg-background px-2 py-1 text-sm"
                  aria-label={t("seed_fixed")}
                />
              )}
            </div>
            <p className="text-xs text-muted-foreground">{t("seed_hint")}</p>
          </section>

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              disabled={!canGenerate}
              onClick={() => create.mutate([strength])}
            >
              {create.isPending ? (
                <Spinner className="size-4" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              {t("generate")}
            </Button>
            {sweep.length > 1 && (
              <Button
                type="button"
                variant="outline"
                disabled={!canGenerate || noLora}
                onClick={() => create.mutate(sweep)}
                title={sweep.map((s) => s.toFixed(1)).join(" · ")}
              >
                {t("sweep", {
                  values: sweep.map((s) => s.toFixed(1)).join(" · "),
                })}
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {t("production_first")}
          </p>
        </CardContent>
      </Card>

      <div className="space-y-4 min-w-0">
        <ResultView
          job={selected}
          onCancel={(job) => cancel.mutate(job)}
          cancelling={cancel.isPending}
        />
        <History
          jobs={jobs}
          loading={history.isLoading}
          error={history.error as Error | null}
          selectedId={selected?.job_id ?? null}
          onSelect={setSelectedId}
        />
      </div>
    </div>
  );
};

const ResultView = ({
  job,
  onCancel,
  cancelling,
}: {
  job: PlaygroundJob | null;
  onCancel: (job: PlaygroundJob) => void;
  cancelling: boolean;
}) => {
  const t = useT();
  const now = useNow(Boolean(job && isActive(job)));
  if (!job) {
    return (
      <Card>
        <CardContent className="py-16 text-center text-sm text-muted-foreground">
          {t("empty")}
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardContent className="py-4 space-y-3">
        <JobSummary job={job} />
        {job.state === "done" && job.output_url ? (
          <BeforeAfter before={job.input_url ?? null} after={job.output_url} />
        ) : (
          <div className="relative rounded-md overflow-hidden bg-black/90 min-h-64 flex items-center justify-center">
            {job.input_url && (
              <img
                src={job.input_url}
                alt=""
                className="max-h-[60vh] w-full object-contain opacity-40"
              />
            )}
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white text-sm">
              {isActive(job) && <Spinner className="size-6 text-white" />}
              <span>{t(stateKey(job))}</span>
              {job.message && !isActive(job) && (
                <span className="max-w-md text-center text-xs text-white/80">
                  {job.message}
                </span>
              )}
              {isActive(job) && (
                <span className="text-xs text-white/70 tabular-nums">
                  {t("elapsed", {
                    seconds: elapsedSeconds(job.created_at, now),
                  })}
                </span>
              )}
              {(job.state === "queued" || job.state === "waiting_gpu") && (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={cancelling}
                  onClick={() => onCancel(job)}
                >
                  <X className="h-3 w-3" /> {t("cancel")}
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const JobSummary = ({ job }: { job: PlaygroundJob }) => {
  const t = useT();
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <Badge>{job.lora?.label ?? job.lora?.id ?? t("no_lora")}</Badge>
      {job.lora?.file && (
        <Badge variant="secondary">
          {t("strength_value", { value: job.strength.toFixed(2) })}
        </Badge>
      )}
      <Badge variant="outline">
        {job.prompt_id === "custom" ? t("custom") : job.prompt_id}
      </Badge>
      <span className="text-muted-foreground" title={job.prompt}>
        {t("seed_value", { seed: job.seed })}
      </span>
      {job.state === "done" && (
        <span className="text-muted-foreground">
          {t("timing", {
            render: Math.round(job.render_seconds ?? 0),
            wait: Math.round(job.gpu_wait_seconds ?? 0),
          })}
        </span>
      )}
    </div>
  );
};

/** Drag to compare the input with the generation (same idea as the entry's Wipe tab). */
const BeforeAfter = ({
  before,
  after,
}: {
  before: string | null;
  after: string;
}) => {
  const t = useT();
  const [position, setPosition] = useState(50);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  return (
    <div className="space-y-2">
      <div
        className="relative w-full max-h-[75vh] bg-black/90 rounded-md overflow-hidden select-none"
        style={{ aspectRatio: size ? `${size.w} / ${size.h}` : "4 / 3" }}
        onMouseMove={(e) => {
          if (e.buttons !== 1) return;
          const rect = e.currentTarget.getBoundingClientRect();
          setPosition(Math.round(((e.clientX - rect.left) / rect.width) * 100));
        }}
      >
        <img
          src={after}
          alt={t("after")}
          className="absolute inset-0 w-full h-full object-contain"
          draggable={false}
          onLoad={(e) =>
            setSize({
              w: e.currentTarget.naturalWidth,
              h: e.currentTarget.naturalHeight,
            })
          }
        />
        {before && (
          <img
            src={before}
            alt={t("before")}
            className="absolute inset-0 w-full h-full object-contain"
            style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
            draggable={false}
          />
        )}
        {before && (
          <div
            className="absolute inset-y-0 w-0.5 bg-white/80 pointer-events-none"
            style={{ left: `${position}%` }}
          />
        )}
        <span className="absolute top-2 left-2 text-xs bg-black/60 text-white px-2 py-0.5 rounded">
          {t("before")}
        </span>
        <span className="absolute top-2 right-2 text-xs bg-black/60 text-white px-2 py-0.5 rounded">
          {t("after")}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <input
          type="range"
          min={0}
          max={100}
          value={position}
          onChange={(e) => setPosition(Number(e.target.value))}
          className="w-64"
          aria-label={t("wipe")}
        />
        <a
          href={after}
          target="_blank"
          rel="noreferrer"
          className="text-xs underline"
        >
          {t("open_full")}
        </a>
      </div>
    </div>
  );
};

const History = ({
  jobs,
  loading,
  error,
  selectedId,
  onSelect,
}: {
  jobs: PlaygroundJob[];
  loading: boolean;
  error: Error | null;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) => {
  const t = useT();
  const batches = useMemo(() => groupBatches(jobs), [jobs]);
  return (
    <Card>
      <CardContent className="py-4 space-y-3">
        <h3 className="text-sm font-semibold">{t("history")}</h3>
        {loading && <Spinner className="size-4" />}
        {error && (
          <p className="text-sm text-destructive">{`${t("history_error")}: ${error.message}`}</p>
        )}
        {!loading && !error && batches.length === 0 && (
          <p className="text-sm text-muted-foreground">{t("history_empty")}</p>
        )}
        {batches.map((batch) => {
          const first = batch.jobs[0];
          return (
            <div key={batch.batch_id} className="space-y-1">
              <p className="text-xs text-muted-foreground">
                {new Date(batch.created_at).toLocaleString()} ·{" "}
                {first.lora?.label ?? t("no_lora")} ·{" "}
                {first.prompt_id === "custom" ? t("custom") : first.prompt_id} ·{" "}
                {t("seed_value", { seed: first.seed })}
                {first.created_by ? ` · ${first.created_by}` : ""}
              </p>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {batch.jobs.map((job) => (
                  <button
                    key={job.job_id}
                    type="button"
                    onClick={() => onSelect(job.job_id)}
                    className={`shrink-0 rounded-md overflow-hidden border-2 text-left ${
                      job.job_id === selectedId
                        ? "border-primary"
                        : "border-transparent hover:border-muted-foreground/40"
                    }`}
                  >
                    <div className="h-24 w-32 bg-black/90 flex items-center justify-center">
                      {job.state === "done" && job.output_url ? (
                        <img
                          src={job.output_url}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      ) : isActive(job) ? (
                        <Spinner className="size-5 text-white" />
                      ) : (
                        <span className="text-[11px] text-white/80 px-2 text-center">
                          {t(stateKey(job))}
                        </span>
                      )}
                    </div>
                    <div className="px-1 py-0.5 text-[11px] tabular-nums">
                      {job.lora?.file ? job.strength.toFixed(2) : t("no_lora")}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};
